<template>
  <div class="irp-contacts-tab" data-testid="irp-contacts-tab">
    <template v-if="!hasError">
      <h3 class="irp-contacts-tab__heading">Your Contacts</h3>
      <div :class="{ 'irp-contacts-tab__card': hasOwnContacts }">
        <div
          v-if="isInitialLoading"
          class="irp-contacts-tab__skeleton"
          data-testid="irp-contacts-tab-skeleton"
        >
          <BittsBone v-for="row in SKELETON_ROW_COUNT" :key="row" type="copy" />
        </div>
        <IRPContactList
          v-else
          :contacts="ownContacts"
          :is-own-data="true"
          empty-message="No contacts on this record"
        />
      </div>
    </template>

    <IRPPartnerDataSection
      :key="individualRecordStore.recordKey"
      section="contacts"
    >
      <template #default="{ partner }">
        <div class="irp-contacts-tab__card">
          <IRPContactList
            :contacts="partnerContacts(partner)"
            :is-own-data="false"
          />
        </div>
      </template>
    </IRPPartnerDataSection>
  </div>
</template>

<script setup lang="ts">
import { BittsBone } from '@crossbeam/bitts';

import { computed } from 'vue';

import { useIndividualRecordStore } from '@/stores';
import type {
  RecordContactCollectionType,
  RecordPartnerType,
} from '@/types/individual_record';

import IRPContactList from './IRPContactList.vue';
import IRPPartnerDataSection from './IRPPartnerDataSection.vue';

const SKELETON_ROW_COUNT = 3;

const individualRecordStore = useIndividualRecordStore();

const isInitialLoading = computed(
  () =>
    !!individualRecordStore.contacts?.isLoading &&
    !individualRecordStore.contacts?.hasLoaded,
);

const hasError = computed(() => !!individualRecordStore.contacts?.error);

const ownContacts = computed(
  () =>
    (
      individualRecordStore.contacts?.own?.contacts as
        | RecordContactCollectionType
        | undefined
    )?.items ?? [],
);

const hasOwnContacts = computed(
  () => isInitialLoading.value || !!ownContacts.value.length,
);

function partnerContacts(partner: RecordPartnerType<'contacts'>) {
  return (
    (partner.contacts as RecordContactCollectionType | undefined)?.items ?? []
  );
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-contacts-tab {
  @apply flex flex-col gap-16;
}

.irp-contacts-tab__heading {
  @apply text-base font-bold text-neutral-text-strong;
}

.irp-contacts-tab__card {
  @apply rounded-16 border border-neutral-border bg-white py-24 pl-24 pr-16 shadow-component;
}

.irp-contacts-tab__skeleton {
  @apply flex flex-col gap-16;
}
</style>
